// feedback/lib/markdown.tsx — hand-rolled markdown-subset renderer.
//
// Repo DNA: fully-custom components, no heavy deps (no marked/remark/DOMPurify).
// Supports EXACTLY: **bold**, *italic*, - and 1. lists, `inline code`, ```
// fenced code blocks, [text](https://…) links (https/http only), and
// ![alt](url) images (same-origin/R2 attachment URLs only). Unicode emoji
// pass through as plain text — nothing special to do. Everything else
// (including anything that looks like raw HTML — `<script>`, `<b>`, …) is
// NEVER interpreted: this module never calls dangerouslySetInnerHTML, so any
// stray text renders as literal, auto-escaped text via React's normal
// children rendering. That single fact is the whole XSS story here.
//
// Shared by the feedback item description AND comments (both the detail page
// and the editor's live-preview toggle) — one parser, one set of security
// rules, everywhere markdown-subset content is read back.

import { Fragment, useMemo } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';

// ── link / image safety ─────────────────────────────────────────────────────

/** Only http(s) links render as an anchor — anything else (javascript:,
 * data:, mailto:, a bare relative path, …) falls back to literal text.
 * Takes the value the CALLER already trimmed — see matchLinkLike, which
 * trims once at extraction so validation and actual use never see two
 * different strings (2026-08-10 second adversarial review, finding W6). */
export function isSafeLinkHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/** Build-time host allowlist for cross-origin attachment images (2026-08-10
 * second adversarial review, finding W3): a registrable-suffix match on
 * "*.r2.dev" let ANY Cloudflare R2 bucket's public host through, including
 * one an attacker controls — only the EXACT host this deployment's own R2
 * bucket serves from is trusted. Set VITE_MEDIA_ORIGIN at build time to the
 * same value as the backend's R2_PUBLIC_URL (see backend/config/settings.py)
 * — e.g. "https://pub-xxxx.r2.dev". Empty/unset means "trust nothing beyond
 * same-origin", which is the safe default for a build that hasn't set it. */
const ALLOWED_MEDIA_ORIGIN = (
  (import.meta.env.VITE_MEDIA_ORIGIN as string | undefined) ?? ''
).trim();

/** A same-origin image is further restricted to paths OUTSIDE the API
 * namespace (2026-08-10 second adversarial review, finding W8) — an <img>
 * pointed at `/api/...` could trigger a side-effecting GET or leak
 * authenticated response data through timing/size, neither of which an
 * inline "picture" has any business doing. */
function isDisallowedSameOriginPath(pathname: string): boolean {
  return pathname.startsWith('/api/');
}

/**
 * Only a same-origin relative/absolute URL (outside /api/…), or the exact
 * configured media host (ALLOWED_MEDIA_ORIGIN), may render as an <img>.
 * Everything else — including a same-scheme off-origin host, a backslash-
 * authority trick ("/\evil.com/x"), or a protocol-relative URL
 * ("//evil.com/x") — falls back to literal text. `origin` and
 * `allowedMediaOrigin` are injectable for tests; default to the real page
 * origin and the build-time constant above.
 *
 * ALWAYS resolves through `new URL` before deciding anything (2026-08-10
 * second adversarial review, finding W1) — an earlier `startsWith('/')`
 * shortcut let "/\evil.com/x.png" through unresolved: browsers (and the
 * WHATWG URL parser) treat a backslash right after a leading slash as an
 * authority separator exactly like "//", so that string actually resolves
 * OFF-ORIGIN despite visually starting with a single "/". Only a real
 * same-origin resolution (or the exact allowed media host) is trusted now.
 */
export function isSafeImageSrc(
  src: string,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
  allowedMediaOrigin: string = ALLOWED_MEDIA_ORIGIN,
): boolean {
  if (!src) return false;
  let u: URL;
  try {
    u = new URL(src, origin || 'http://localhost');
  } catch {
    return false;
  }
  if (!/^https?:$/i.test(u.protocol)) return false;
  if (origin && u.origin === origin) {
    return !isDisallowedSameOriginPath(u.pathname);
  }
  if (allowedMediaOrigin && u.origin === allowedMediaOrigin) return true;
  return false;
}

/** Strips zero-width and bidi-override/isolate characters from rendered
 * link text (2026-08-10 second adversarial review, finding W7) — these can
 * make a link's VISIBLE text read as something other than its actual
 * content (e.g. an RTL override flipping "moc.live" into what looks like
 * "evil.com" reversed, or a zero-width character splitting a blocklisted
 * word). The href itself is validated separately (isSafeLinkHref); this is
 * about what the human reading the rendered text actually sees. Built from
 * explicit codepoints via String.fromCodePoint() (never as literal/escaped
 * characters in this source file) so the invisible characters this regex
 * targets can never themselves get silently mangled by an editor/encoding
 * round-trip. */
const BIDI_OVERRIDE_CODEPOINTS = [
  0x200b, // ZERO WIDTH SPACE
  0x200e, // LEFT-TO-RIGHT MARK
  0x200f, // RIGHT-TO-LEFT MARK
  0x202a, // LEFT-TO-RIGHT EMBEDDING
  0x202b, // RIGHT-TO-LEFT EMBEDDING
  0x202c, // POP DIRECTIONAL FORMATTING
  0x202d, // LEFT-TO-RIGHT OVERRIDE
  0x202e, // RIGHT-TO-LEFT OVERRIDE
  0x2066, // LEFT-TO-RIGHT ISOLATE
  0x2067, // RIGHT-TO-LEFT ISOLATE
  0x2068, // FIRST STRONG ISOLATE
  0x2069, // POP DIRECTIONAL ISOLATE
];
const BIDI_OVERRIDE_RE = new RegExp(
  `[${BIDI_OVERRIDE_CODEPOINTS.map((c) => String.fromCodePoint(c)).join('')}]`,
  'g',
);

export function stripBidiOverrides(text: string): string {
  return text.replace(BIDI_OVERRIDE_RE, '');
}

// ── inline tokens ────────────────────────────────────────────────────────────

export type InlineToken =
  | { type: 'text'; value: string }
  | { type: 'bold'; children: InlineToken[] }
  | { type: 'italic'; children: InlineToken[] }
  | { type: 'code'; value: string }
  | { type: 'link'; href: string; children: InlineToken[] }
  | { type: 'image'; src: string; alt: string };

interface LinkLikeMatch {
  label: string;
  url: string;
  /** Index just past the closing ")" — the caller resumes scanning here. */
  end: number;
}

/** Parses `[label](url)` starting at `text[start] === '['`. Non-greedy on
 * both the label (up to the first `]`) and the url (up to the first `)`
 * after it) — no nested-bracket support, matching the "subset" scope. Returns
 * null (never throws) on anything malformed, e.g. no closing `]`, no `(` right
 * after it, or no closing `)` — the caller treats that as "not a link". */
function matchLinkLike(text: string, start: number): LinkLikeMatch | null {
  const closeBracket = text.indexOf(']', start + 1);
  if (closeBracket === -1) return null;
  if (text[closeBracket + 1] !== '(') return null;
  const closeParen = text.indexOf(')', closeBracket + 2);
  if (closeParen === -1) return null;
  return {
    label: text.slice(start + 1, closeBracket),
    // Trimmed once, here, at extraction — every caller both VALIDATES and
    // USES this same trimmed value (2026-08-10 second adversarial review,
    // finding W6), so there is no path where the safety check sees one
    // string and the rendered href/src ends up being a different one.
    url: text.slice(closeBracket + 2, closeParen).trim(),
    end: closeParen + 1,
  };
}

/**
 * Parses one line/paragraph's worth of text (no block-level tokens) into
 * inline tokens. Deliberately hand-scanned (not a chain of regexes) so
 * nested/unclosed delimiters degrade to literal text instead of exponential
 * backtracking or mis-nesting — every branch either advances `i` or falls
 * through to appending one literal character, so this always terminates in
 * bounded time relative to input length. An invalid link/image href doesn't
 * drop the token — it renders the ORIGINAL `[label](url)` / `![alt](url)`
 * text literally, so a rejected link is still visible, just not clickable.
 */
export function parseInline(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let i = 0;
  let buf = '';

  function flush() {
    if (buf) {
      tokens.push({ type: 'text', value: buf });
      buf = '';
    }
  }

  while (i < text.length) {
    const ch = text[i];

    if (ch === '!' && text[i + 1] === '[') {
      const m = matchLinkLike(text, i + 1);
      if (m) {
        flush();
        if (isSafeImageSrc(m.url)) {
          tokens.push({ type: 'image', src: m.url, alt: stripBidiOverrides(m.label) });
        } else {
          tokens.push({ type: 'text', value: text.slice(i, m.end) });
        }
        i = m.end;
        continue;
      }
    }

    if (ch === '[') {
      const m = matchLinkLike(text, i);
      if (m) {
        flush();
        if (isSafeLinkHref(m.url)) {
          // The VISIBLE text is bidi/zero-width-stripped (W7) — the href
          // itself is untouched, only what a reader sees is sanitized.
          tokens.push({
            type: 'link',
            href: m.url,
            children: parseInline(stripBidiOverrides(m.label)),
          });
        } else {
          tokens.push({ type: 'text', value: text.slice(i, m.end) });
        }
        i = m.end;
        continue;
      }
    }

    if (ch === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        flush();
        tokens.push({ type: 'code', value: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (ch === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        flush();
        tokens.push({ type: 'bold', children: parseInline(text.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
      // No closing "**" anywhere ahead — both stars are literal. Consume them
      // together here so the single-* italic check below never re-reads the
      // second star as its own (wrongly empty) italic closing delimiter.
      buf += '**';
      i += 2;
      continue;
    }

    if (ch === '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        flush();
        tokens.push({ type: 'italic', children: parseInline(text.slice(i + 1, end)) });
        i = end + 1;
        continue;
      }
      buf += ch;
      i++;
      continue;
    }

    buf += ch;
    i++;
  }
  flush();
  return tokens;
}

function renderInline(tokens: InlineToken[], keyPrefix: string): ReactNode[] {
  return tokens.map((t, idx) => {
    const key = `${keyPrefix}-${idx}`;
    switch (t.type) {
      case 'text':
        return t.value ? <Fragment key={key}>{t.value}</Fragment> : null;
      case 'bold':
        return <strong key={key}>{renderInline(t.children, key)}</strong>;
      case 'italic':
        return <em key={key}>{renderInline(t.children, key)}</em>;
      case 'code':
        return <InlineCode key={key}>{t.value}</InlineCode>;
      case 'link':
        return (
          <a key={key} href={t.href} target="_blank" rel="noopener noreferrer">
            {renderInline(t.children, key)}
          </a>
        );
      case 'image':
        return <InlineImage key={key} src={t.src} alt={t.alt} />;
      default:
        return null;
    }
  });
}

// ── block tokens ─────────────────────────────────────────────────────────────

type Block =
  | { type: 'paragraph'; lines: string[] }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'code'; content: string };

const FENCE_RE = /^```/;
const UL_ITEM_RE = /^-\s+(.*)$/;
const OL_ITEM_RE = /^\d+\.\s+(.*)$/;

/**
 * Splits a markdown-subset source string into block-level tokens. Every loop
 * here strictly advances `i` at least once per iteration (an unterminated
 * fence just consumes to EOF instead of looping forever), so this always
 * terminates. Exported for direct unit testing of the block grammar.
 */
export function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    if (FENCE_RE.test(line.trim())) {
      i++;
      const content: string[] = [];
      while (i < lines.length && !FENCE_RE.test(lines[i]!.trim())) {
        content.push(lines[i]!);
        i++;
      }
      // Unterminated fence: still render what was collected — a missing
      // closing ``` must not swallow the rest of the document.
      if (i < lines.length) i++; // skip the closing fence line
      blocks.push({ type: 'code', content: content.join('\n') });
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    if (UL_ITEM_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = UL_ITEM_RE.exec(lines[i]!);
        if (!m) break;
        items.push(m[1]!);
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (OL_ITEM_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = OL_ITEM_RE.exec(lines[i]!);
        if (!m) break;
        items.push(m[1]!);
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() !== '' &&
      !FENCE_RE.test(lines[i]!.trim()) &&
      !UL_ITEM_RE.test(lines[i]!) &&
      !OL_ITEM_RE.test(lines[i]!)
    ) {
      paraLines.push(lines[i]!);
      i++;
    }
    blocks.push({ type: 'paragraph', lines: paraLines });
  }

  return blocks;
}

function renderBlocks(blocks: Block[]): ReactNode[] {
  return blocks.map((b, idx) => {
    const key = `blk-${idx}`;
    if (b.type === 'code') {
      return (
        <CodeBlock key={key}>
          <code>{b.content}</code>
        </CodeBlock>
      );
    }
    if (b.type === 'ul') {
      return (
        <UnorderedList key={key}>
          {b.items.map((it, i2) => (
            <li key={i2}>{renderInline(parseInline(it), `${key}-${i2}`)}</li>
          ))}
        </UnorderedList>
      );
    }
    if (b.type === 'ol') {
      return (
        <OrderedList key={key}>
          {b.items.map((it, i2) => (
            <li key={i2}>{renderInline(parseInline(it), `${key}-${i2}`)}</li>
          ))}
        </OrderedList>
      );
    }
    return (
      <Paragraph key={key}>
        {b.lines.map((line, i2) => (
          <Fragment key={i2}>
            {i2 > 0 && <br />}
            {renderInline(parseInline(line), `${key}-${i2}`)}
          </Fragment>
        ))}
      </Paragraph>
    );
  });
}

// ── styled primitives ────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Paragraph = styled.p`
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.6;
`;

const listStyles = `
  margin: 0;
  padding-left: 20px;
  line-height: 1.6;
`;

const UnorderedList = styled.ul`
  ${listStyles}
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const OrderedList = styled.ol`
  ${listStyles}
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const CodeBlock = styled.pre`
  margin: 0;
  padding: 10px 12px;
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  font-family: monospace;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const InlineCode = styled.code`
  padding: 1px 5px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.glassBg};
  font-family: monospace;
  font-size: 0.92em;
`;

const InlineImage = styled.img`
  max-width: 100%;
  border-radius: ${({ theme }) => theme.radius.md};
  display: block;
`;

// ── component ─────────────────────────────────────────────────────────────────

export interface FeedbackMarkdownProps {
  source: string;
  className?: string;
}

/** Renders a markdown-subset string — shared by the item description and
 * comments, in both the detail page and the editor's preview toggle. */
export function FeedbackMarkdown({ source, className }: FeedbackMarkdownProps) {
  const blocks = useMemo(() => parseBlocks(source ?? ''), [source]);
  if (!source?.trim()) return null;
  return <Root className={className}>{renderBlocks(blocks)}</Root>;
}
